import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { createPageUrl } from '@/utils';
import SEOHead from '@/components/SEOHead';

const TRACKS = [
  {
    title: 'AI-Assisted Web Development',
    desc: 'Build production websites and web apps with modern AI tooling — prompting, refining, and shipping faster than traditional workflows.',
  },
  {
    title: 'Frontend Engineering',
    desc: 'HTML, CSS, JavaScript and React — from your first page to component-driven interfaces that feel professional.',
  },
  {
    title: 'Backend & APIs',
    desc: 'Node.js, databases, authentication and payments — everything a real product needs behind the screen.',
  },
  {
    title: 'Shipping & Freelancing',
    desc: 'Deploy, get clients, price your work and get paid — the business skills most courses skip.',
  },
];

const FORMAT = [
  ['Cohort-based', 'Learn with a group, not alone. Fixed start dates, shared momentum, real accountability.'],
  ['Project-first', 'Every module ends with something deployed and live — your portfolio grows as you learn.'],
  ['Mentor reviews', 'Working developers review your code and projects, one-on-one.'],
  ['Built for Africa, open to all', 'Naira-friendly tuition, remote-first, and focused on skills that win global work.'],
];

const PROJECT_IMAGES = [
  { src: '/images/showcase/pulseboard.jpg', alt: 'Analytics dashboard student project' },
  { src: '/images/showcase/aurelia.jpg', alt: 'E-commerce storefront student project' },
  { src: '/images/showcase/nebula.jpg', alt: 'Web3 dashboard student project' },
];

export default function Academy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <SEOHead
        title="BuildersLab Academy — Training School for Developers"
        description="BuildersLab Academy is a hands-on training school for developers: AI-assisted web development, frontend, backend and freelancing skills. Coming soon — apply at academy.builderslab.net."
        ogUrl="https://builderslab.net/Academy"
      />

      {/* ── Hero with background image ── */}
      <div className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/showcase/pulseboard.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/85 via-zinc-950/90 to-zinc-950" />

        <div className="relative max-w-4xl mx-auto px-6 pt-40 pb-24 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-500 text-ink text-xs font-bold uppercase tracking-widest mb-6">
            Coming Soon
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
            BuildersLab <span className="text-orange-500">Academy</span>
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
            A training school for developers. Learn to build and ship real software —
            with AI as your co-pilot, mentors at your side, and a portfolio that speaks for itself.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://academy.builderslab.net" target="_blank" rel="noopener noreferrer">
              <Button className="bg-orange-500 hover:bg-orange-400 text-ink font-bold rounded-xl h-12 px-8 text-base">
                Apply Now →
              </Button>
            </a>
            <span className="text-zinc-500 text-sm">First cohort announcement coming soon</span>
          </div>
        </div>
      </div>

      {/* ── What you'll learn ── */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-center mb-4">
          What you'll <span className="text-orange-500">learn</span>
        </h2>
        <p className="text-zinc-400 text-center max-w-2xl mx-auto mb-12">
          Four tracks, one goal: turn you into a developer who ships.
        </p>
        <div className="grid sm:grid-cols-2 gap-5">
          {TRACKS.map((t, i) => (
            <div key={t.title} className="rounded-xl border border-zinc-800 bg-zinc-900 p-7">
              <span className="text-orange-500 font-extrabold text-sm">0{i + 1}</span>
              <h3 className="text-white font-bold text-lg mt-2 mb-2">{t.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Learn by building ── */}
      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 md:p-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-4">
                Learn by building <span className="text-orange-500">real products</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                No toy exercises. From week one you'll build the kind of software people
                actually pay for — dashboards, storefronts, web apps — using the same
                platform and workflows behind BuildersLab itself.
              </p>
              <ul className="space-y-2.5">
                {FORMAT.map(([title, desc]) => (
                  <li key={title} className="flex items-start gap-2.5 text-sm leading-relaxed">
                    <span className="text-orange-400 shrink-0">✓</span>
                    <span className="text-zinc-300"><strong className="text-white">{title}.</strong> {desc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img
                src={PROJECT_IMAGES[0].src}
                alt={PROJECT_IMAGES[0].alt}
                loading="lazy"
                className="rounded-lg border border-zinc-800 col-span-2 w-full aspect-[16/9] object-cover object-top"
              />
              <img
                src={PROJECT_IMAGES[1].src}
                alt={PROJECT_IMAGES[1].alt}
                loading="lazy"
                className="rounded-lg border border-zinc-800 w-full aspect-[4/3] object-cover object-top"
              />
              <img
                src={PROJECT_IMAGES[2].src}
                alt={PROJECT_IMAGES[2].alt}
                loading="lazy"
                className="rounded-lg border border-zinc-800 w-full aspect-[4/3] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Final CTA ── */}
      <div className="max-w-4xl mx-auto px-6 pb-28 text-center">
        <div className="rounded-xl border-2 border-orange-500/40 bg-zinc-900 p-10 md:p-14">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
            Be first in the <span className="text-orange-500">first cohort</span>
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-8 leading-relaxed">
            BuildersLab Academy is launching soon. Applications are open now — seats in
            the first cohort will be limited, and early applicants get priority.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://academy.builderslab.net" target="_blank" rel="noopener noreferrer">
              <Button className="bg-orange-500 hover:bg-orange-400 text-ink font-bold rounded-xl h-12 px-8 text-base">
                Apply at academy.builderslab.net →
              </Button>
            </a>
            <Link to={createPageUrl('Contact')} className="text-zinc-400 hover:text-white text-sm transition-colors">
              Questions? Contact us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
